import {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import type { FormEvent } from 'react';
import Cleave from 'cleave.js';

import SimpleStringInput from './SimpleStringInput';
import type { SimpleStringInputProps } from './SimpleStringInput';

// Стабильные идентити-функции для дефолтов: создаются один раз на уровне модуля,
// иначе инлайновые дефолты (v => v) порождали новую ссылку на каждом рендере
// и заставляли useEffect (с formatFormValue/formatDisplayValue в deps) сбрасывать введённое значение.
const identityFn = (v: any) => v;

/**
 * Событие onChange/onComplete SimpleMaskInput — собирается вручную в handleValueChange
 * поверх нативного input-события cleave (см. doc/options.md#onvaluechanged).
 *
 * `rawValue` на верхнем уровне объявлен ради MaskInput, который исторически читает
 * `event?.rawValue` (не `event.target.rawValue`): значение там всегда undefined.
 * Поведение не меняем — миграция типов.
 */
export interface MaskChangeEvent {
  target: {
    name?: string;
    value: string;
    rawValue?: string;
  };
  rawValue?: string;
}

/**
 * Пропсы «глупого» masked-инпута (без Formik). Formik-обёртки — MaskInput, PhoneInput.
 *
 * `value`/`disabled`/`readOnly`/`autoComplete`/`onChange` переопределяются мягче базового
 * Omit'ом: маска работает со строками, onChange получает синтетический MaskChangeEvent.
 */
export interface SimpleMaskInputProps extends Omit<SimpleStringInputProps,
  'value' | 'disabled' | 'readOnly' | 'autoComplete' | 'onChange'> {
  /** Значение формы (сырое, без маски). */
  value?: string | null;
  /** Маска ввода, напр. '(000) 000-00-00'. @default '' */
  maskPattern?: string;
  /** Префикс, напр. '+7'. */
  prefix?: string;
  /** Только числа. */
  numericOnly?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  /** Опции, передаваемые в cleave напрямую (@see https://github.com/nosir/cleave.js/blob/master/doc/options.md). */
  options?: Record<string, any>;
  /** Приведение значения для записи в форму (hidden input). @default identity */
  formatFormValue?: (value: any) => any;
  /** Приведение значения для отображения в маске. @default identity */
  formatDisplayValue?: (value: any) => string;
  onChange?: (event: MaskChangeEvent) => void;
  /** Срабатывает, когда маска заполнена полностью. */
  onComplete?: (event: MaskChangeEvent) => void;
  /** Ручной ввод: получает maskedValue и rawValue. */
  onManualChange?: (maskedValue: string, rawValue?: string) => void;
}

/**
 * Императивный ref SimpleMaskInput: спред экземпляра cleave (getRawValue/setRawValue/…)
 * плюс два собственных геттера.
 */
export interface SimpleMaskInputRef {
  getInputElement: () => HTMLInputElement | null;
  getMaskedValue: () => string | undefined;
  // Прочие члены — из `...masked.current` (cleave).
  [key: string]: any;
}

const SimpleMaskInput = forwardRef<SimpleMaskInputRef, SimpleMaskInputProps>((props, ref) => {
  const input = useRef<HTMLInputElement | null>(null);
  const masked = useRef<Cleave | null>(null);
  const [inputFormValue, setInputFormValue] = useState('');

  const {
    autoComplete,
    value,
    maskPattern = '',
    numericOnly,
    prefix,
    disabled,
    readOnly,
    options,
    formatFormValue = identityFn,
    formatDisplayValue = identityFn,
    onChange,
    onComplete,
    onManualChange,
    ...rest
  } = props;

  const blocks = options?.blocks || maskPattern.split(/\W|\s/).map(b => b.length);
  const delimiters = Array.from(maskPattern.replace(/\w\d*/ig, ''));

  if (prefix) {
    blocks.unshift(prefix.length);
    delimiters.unshift(prefix.replace(/.*/, ' '));
  }

  const handleValueChange = (event: FormEvent<HTMLInputElement>) => {
    event.persist();
    const { value: maskedValue } = event.target as HTMLInputElement;
    const rawValue = masked.current?.getRawValue();

    const maskEvent: MaskChangeEvent = {
      target: {
        name: props.name,
        value: maskedValue,
        rawValue: rawValue,
      },
    };

    if (readOnly || disabled || (value === maskedValue)) {
      return;
    }

    // Вызываем обработчик ручного ввода, если передан
    if (typeof onManualChange === 'function') {
      onManualChange(maskedValue, rawValue);
    }

    if (typeof onChange === 'function') {
      onChange(maskEvent);
    }

    // queueMicrotask вместо process.nextTick: в браузере nextTick — полифилл (Rsbuild),
    // queueMicrotask — нативный API с той же семантикой (микрозадача после текущего синхронного цикла).
    queueMicrotask(() => {
      if ((typeof onComplete === 'function') && (maskedValue?.length === maskPattern?.length)) {
        onComplete(maskEvent);
      }
    });

  };

  useImperativeHandle(ref, () => ({
    ...masked.current,
    // Добавляем метод для прямого доступа к input
    getInputElement: () => input.current,
    getMaskedValue: () => masked.current?.getRawValue(),
  }));

  useEffect(() => {
    if (input.current && !masked.current) {
      masked.current = new Cleave(input.current, {
        prefix,
        blocks,
        numericOnly,
        delimiters,
        initValue: value,
        delimiterLazyShow: true,
        ...options,
      });
    }

    return () => {
      if (typeof masked.current?.destroy === 'function') {
        masked.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, masked]);

  useEffect(() => {
    const formValue = formatFormValue(value || '');
    const displayValue = formatDisplayValue(value || '');

    setInputFormValue(formValue);
    masked.current?.setRawValue(displayValue);
  }, [value, masked, formatFormValue, formatDisplayValue]);

  return (
    <>
      <SimpleStringInput
        { ...rest }
        ref={ input }
        disabled={ disabled }
        readOnly={ readOnly }
        autoComplete={ autoComplete || 'off' }
        onInput={ handleValueChange }
      />

      <input type='hidden' name={ props.name } value={ inputFormValue } />
    </>
  );
});

export default SimpleMaskInput;
